"use client";
import React from "react";

// Types for Smart Edit session
export interface SmartEditSession {
  tempPath: string;
  watchId: number;
  unsubscribe: () => void;
  documentName: string;
}

export interface UseSmartEditOptions {
  editableFileTypes?: string[];
  tempSubDir?: string;
  onFileChanged?: React.RefObject<((contentItemID: string, tempPath: string, documentName: string) => void) | undefined>;
  onEditStarted?: React.RefObject<((contentItemID: string) => void) | undefined>;
  onEditCancelled?: React.RefObject<((contentItemID: string) => void) | undefined>;
  onError?: React.RefObject<((error: string) => void) | undefined>;
}

export interface UseSmartEditReturn {
  editInProgress: boolean;
  editInProgressContentItemID: string | null;
  startEdit: (contentItemID: string, documentName: string, documentType: string) => Promise<void>;
  cancelEdit: (contentItemID: string) => Promise<void>;
  checkFileOpen: (tempPath: string) => Promise<{ isOpen: boolean }>;
  cleanupAll: () => void;
}

const DEFAULT_EDITABLE_FILE_TYPES = ["docx", "xlsx"];
const DEFAULT_TEMP_SUBDIR = "novus";

export function useSmartEdit(options: UseSmartEditOptions = {}): UseSmartEditReturn {
  const {
    editableFileTypes = DEFAULT_EDITABLE_FILE_TYPES,
    tempSubDir = DEFAULT_TEMP_SUBDIR,
    onFileChanged,
    onEditStarted,
    onEditCancelled,
    onError,
  } = options;

  // Using ref to avoid stale closures in IPC callbacks
  const smartEditSessions = React.useRef<Map<string, SmartEditSession>>(new Map());
  const [editInProgressContentItemID, setEditInProgressContentItemID] = React.useState<string | null>(null);

  const startEdit = React.useCallback(
    async (contentItemID: string, documentName: string, documentType: string) => {
      // Check if already editing
      if (smartEditSessions.current.has(contentItemID)) {
        return;
      }

      setEditInProgressContentItemID(contentItemID);
      onEditStarted?.current?.(contentItemID);

      try {
        // Download the file from server using contentItemID
        const downloadUrl = `/files/fileDownloadUrl?itemID=${contentItemID}`;
        const downloadResult = await window.electronAPI.downloadFile(downloadUrl);
        if (!downloadResult.success || !downloadResult.arrayBuffer) {
          throw new Error("Failed to download file");
        }

        // Save the file to temp
        const saveResult = await window.electronAPI.saveDragFile(
          downloadResult.arrayBuffer,
          documentName || documentType,
          tempSubDir
        );

        if (!saveResult.tempPath) {
          throw new Error("Failed to save file");
        }

        // Open the file in system app
        await window.electronAPI.openFile(saveResult.tempPath);

        // Start watching for changes
        const watchResult = await window.electronAPI.watchFile(saveResult.tempPath);

        // Store session
        smartEditSessions.current.set(contentItemID, {
          tempPath: saveResult.tempPath!,
          watchId: watchResult.watchId!,
          unsubscribe: () => window.electronAPI.stopWatchingFile(watchResult.watchId!),
          documentName: documentName || documentType,
        });

        // Listen for file changes
        const unsubscribe = window.electronAPI.onFileChanged((data) => {
          if (data.filePath === saveResult.tempPath) {
            const session = smartEditSessions.current.get(contentItemID);
            if (session && onFileChanged?.current) {
              onFileChanged.current(contentItemID, session.tempPath, session.documentName);
            }
          }
        });

        // Update session with unsubscribe
        const updatedSession = smartEditSessions.current.get(contentItemID);
        if (updatedSession) {
          updatedSession.unsubscribe = () => {
            window.electronAPI.stopWatchingFile(watchResult.watchId!);
            unsubscribe();
          };
        }
      } catch (error: any) {
        console.error("[SmartEdit] Error starting edit:", error);
        onError?.current?.(error.message || "Failed to start edit");
        setEditInProgressContentItemID(null);
      }
    },
    [tempSubDir]
  );

  const cancelEdit = React.useCallback(
    async (contentItemID: string) => {
      const session = smartEditSessions.current.get(contentItemID);
      if (session) {
        // Stop watching
        session.unsubscribe();
        smartEditSessions.current.delete(contentItemID);
        // Clean up temp file
        await window.electronAPI.cleanupFile(session.tempPath);
        onEditCancelled?.current?.(contentItemID);
      }
      setEditInProgressContentItemID(null);
    },
    []
  );

  const checkFileOpen = React.useCallback(async (tempPath: string): Promise<{ isOpen: boolean }> => {
    try {
      const result = await window.electronAPI.checkFileOpen(tempPath);
      return { isOpen: result.isOpen || false };
    } catch {
      return { isOpen: false };
    }
  }, []);

  const cleanupAll = React.useCallback(() => {
    smartEditSessions.current.forEach((session) => {
      session.unsubscribe();
      window.electronAPI.cleanupFile(session.tempPath);
    });
    smartEditSessions.current.clear();
    setEditInProgressContentItemID(null);
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      cleanupAll();
    };
  }, [cleanupAll]);

  return {
    editInProgress: editInProgressContentItemID !== null,
    editInProgressContentItemID,
    startEdit,
    cancelEdit,
    checkFileOpen,
    cleanupAll,
  };
}
